/**
 * UC-182: organisation Sold As / Bought As defaults.
 * Effective values are column pointers. An override is a non-null reference
 * on variant_designation_overrides. A role with no saved organisation default
 * uses the leftmost Variant Mapping column. A missing cell value stays blank.
 */

export const SOLD_AS_FILL = '#C3F0D3';
export const SOLD_AS_TEXT = '#054F31';
export const SOLD_AS_BORDER = '#9FE3BB';
export const BOUGHT_AS_OUTLINE = '#027A48';
export const CELL_BORDER = '#E4E7EC';
export const MISSING_CELL_FILL = '#FFFBFA';
export const MISSING_CELL_OUTLINE = '#D92D20';

export interface DesignationPaint {
  backgroundColor?: string;
  border?: string;
  color?: string;
  borderRadius?: string;
  padding?: string;
}

/** Paint the variant card itself. Matches EVE Variant Display: fill, outline, and missing outline sit on the cell, not the table background. */
export function variantCardStyle(input: {
  sold: boolean;
  bought: boolean;
  missing: boolean;
}): DesignationPaint {
  if (input.missing) {
    return { backgroundColor: MISSING_CELL_FILL, border: `2px solid ${MISSING_CELL_OUTLINE}` };
  }
  if (!input.sold && !input.bought) return {};
  return {
    backgroundColor: input.sold ? SOLD_AS_FILL : '#ffffff',
    border: input.bought ? `2px solid ${BOUGHT_AS_OUTLINE}` : `1px solid ${CELL_BORDER}`,
    color: input.sold ? SOLD_AS_TEXT : '#101828',
  };
}

export function roleHeadingStyle(input: { sold: boolean; bought: boolean }): DesignationPaint {
  if (!input.sold && !input.bought) return {};
  return {
    color: SOLD_AS_TEXT,
    backgroundColor: input.sold ? SOLD_AS_FILL : '#ffffff',
    border: input.bought ? `2px solid ${BOUGHT_AS_OUTLINE}` : `1px solid ${SOLD_AS_BORDER}`,
    borderRadius: '4px',
    padding: '3px 8px',
  };
}

export function roleToggleStyle(input: { active: boolean; role: 'sold' | 'bought' }): DesignationPaint {
  if (input.role === 'sold') {
    return {
      color: input.active ? SOLD_AS_TEXT : '#475467',
      backgroundColor: input.active ? SOLD_AS_FILL : '#ffffff',
      border: input.active ? `1px solid ${SOLD_AS_BORDER}` : '1px solid #d0d5dd',
      borderRadius: '5px',
      padding: '5px 8px',
    };
  }
  return {
    color: input.active ? SOLD_AS_TEXT : '#475467',
    backgroundColor: '#ffffff',
    border: input.active ? `2px solid ${BOUGHT_AS_OUTLINE}` : '1px solid #d0d5dd',
    borderRadius: '5px',
    padding: '5px 8px',
  };
}
export const SET_DEFAULT_MENU_LABEL = 'Set default Sold / Bought as column';
export const APPLY_DEFAULTS_LABEL = 'Apply defaults';
export const CANCEL_LABEL = 'Cancel';
export const APPLY_FAILURE_MESSAGE = 'The defaults were not applied.';
export const CONFLICT_BODY =
  'Changing a default column will undo that work. Choose what happens to those rows after this new default configuration.';
export const FOLLOW_DEFAULTS_LABEL = 'Change only rows that follow the defaults';
export const OVERRIDE_ALL_LABEL = 'Override all rows';

export type DesignationRole = 'sold_as' | 'bought_as';
export type ApplyPolicy = 'follow_defaults' | 'override_all';
export type RoleLabel = 'Sold as' | 'Bought as' | 'Sold / Bought as';
export type HistoryAction = 'override' | 'reset' | 'apply_defaults' | 'changed';

export interface IdentifierRef {
  columnKey: string;
  identifierType: string;
}

export interface DesignationDefaults {
  soldAs: IdentifierRef | null;
  boughtAs: IdentifierRef | null;
}

export interface DesignationVariantRow {
  masterProductId: string;
  eveId: number;
  soldAsOverride: IdentifierRef | null;
  boughtAsOverride: IdentifierRef | null;
  valuesByColumn: Record<string, string | null | undefined>;
}

export interface HistoryEventDraft {
  masterProductId: string;
  action: HistoryAction;
  affectedRoles: DesignationRole[];
  previousRefs: { soldAs: IdentifierRef | null; boughtAs: IdentifierRef | null };
  newRefs: { soldAs: IdentifierRef | null; boughtAs: IdentifierRef | null };
  reason?: string;
}

export interface ConflictCopy {
  title: string;
  body: string;
  followDefaultsDescription: string;
  overrideAllDescription: string;
  count: number;
  roleLabel: RoleLabel;
}

export interface ColumnChoice {
  key: string;
  identifierType: string;
}

type ApplyReady = {
  ok: true;
  conflict: false;
  defaults: DesignationDefaults;
  rows: DesignationVariantRow[];
  events: HistoryEventDraft[];
};

type ApplyConflict = {
  ok: true;
  conflict: true;
  copy: ConflictCopy;
};

type ApplyFailed = {
  ok: false;
  message: string;
};

export type ApplyPlan = ApplyReady | ApplyConflict | ApplyFailed;

export function isBlankValue(value: string | null | undefined): boolean {
  return value == null || value.trim() === '';
}

export function refsEqual(left: IdentifierRef | null, right: IdentifierRef | null): boolean {
  if (left == null || right == null) return left == null && right == null;
  return left.columnKey === right.columnKey && left.identifierType === right.identifierType;
}

export function changedRoles(
  current: DesignationDefaults,
  next: DesignationDefaults,
): DesignationRole[] {
  const roles: DesignationRole[] = [];
  if (!refsEqual(current.soldAs, next.soldAs)) roles.push('sold_as');
  if (!refsEqual(current.boughtAs, next.boughtAs)) roles.push('bought_as');
  return roles;
}

function overrideFor(row: DesignationVariantRow, role: DesignationRole): IdentifierRef | null {
  return role === 'sold_as' ? row.soldAsOverride : row.boughtAsOverride;
}

export function rowsOverridingRoles(
  rows: readonly DesignationVariantRow[],
  roles: readonly DesignationRole[],
): DesignationVariantRow[] {
  if (roles.length === 0) return [];
  return rows.filter((row) => roles.some((role) => overrideFor(row, role) != null));
}

export function roleLabelFor(roles: readonly DesignationRole[]): RoleLabel {
  const sold = roles.includes('sold_as');
  const bought = roles.includes('bought_as');
  if (sold && bought) return 'Sold / Bought as';
  if (bought) return 'Bought as';
  return 'Sold as';
}

export function conflictCopy(count: number, roleLabel: RoleLabel): ConflictCopy {
  const title =
    count === 1
      ? `1 set in this organisation has ${roleLabel} set by hand`
      : `${count} sets in this organisation have ${roleLabel} set by hand`;
  return {
    title,
    body: CONFLICT_BODY,
    followDefaultsDescription: `The ${count} hand-set rows keep their ${roleLabel}. Everything else moves to the new columns.`,
    overrideAllDescription: `Every set in this organisation resets its ${roleLabel} to the new default columns, including the ${count} set by hand.`,
    count,
    roleLabel,
  };
}

function rolesWithOverrides(
  rows: readonly DesignationVariantRow[],
  roles: readonly DesignationRole[],
): DesignationRole[] {
  return roles.filter((role) => rows.some((row) => overrideFor(row, role) != null));
}

function clearRole(row: DesignationVariantRow, role: DesignationRole): DesignationVariantRow {
  if (role === 'sold_as') return { ...row, soldAsOverride: null };
  return { ...row, boughtAsOverride: null };
}

function historyFor(
  row: DesignationVariantRow,
  nextRow: DesignationVariantRow,
  action: HistoryAction,
  roles: DesignationRole[],
  reason?: string,
): HistoryEventDraft {
  return {
    masterProductId: row.masterProductId,
    action,
    affectedRoles: roles,
    previousRefs: { soldAs: row.soldAsOverride, boughtAs: row.boughtAsOverride },
    newRefs: { soldAs: nextRow.soldAsOverride, boughtAs: nextRow.boughtAsOverride },
    reason,
  };
}

export function planApply(input: {
  rows: readonly DesignationVariantRow[];
  current: DesignationDefaults;
  next: DesignationDefaults;
  policy?: ApplyPolicy;
}): ApplyPlan {
  const roles = changedRoles(input.current, input.next);
  if (roles.length === 0) {
    return {
      ok: true,
      conflict: false,
      defaults: input.current,
      rows: input.rows.map((row) => ({ ...row })),
      events: input.rows.map((row) => historyFor(row, row, 'apply_defaults', [])),
    };
  }

  const overriding = rowsOverridingRoles(input.rows, roles);
  if (overriding.length > 0 && input.policy == null) {
    const labeled = rolesWithOverrides(input.rows, roles);
    return {
      ok: true,
      conflict: true,
      copy: conflictCopy(overriding.length, roleLabelFor(labeled)),
    };
  }

  const policy = input.policy ?? 'follow_defaults';
  const rows = input.rows.map((row) => {
    let nextRow = { ...row };
    if (policy === 'override_all') {
      for (const role of roles) nextRow = clearRole(nextRow, role);
    }
    return nextRow;
  });

  return {
    ok: true,
    conflict: false,
    defaults: {
      soldAs: roles.includes('sold_as') ? input.next.soldAs : input.current.soldAs,
      boughtAs: roles.includes('bought_as') ? input.next.boughtAs : input.current.boughtAs,
    },
    rows,
    events: input.rows.map((row, index) => historyFor(row, rows[index], 'apply_defaults', roles)),
  };
}

export function effectiveColumn(
  row: DesignationVariantRow,
  role: DesignationRole,
  defaults: DesignationDefaults,
): IdentifierRef | null {
  const override = overrideFor(row, role);
  if (override != null) return override;
  return role === 'sold_as' ? defaults.soldAs : defaults.boughtAs;
}

export function effectiveCell(
  row: DesignationVariantRow,
  role: DesignationRole,
  defaults: DesignationDefaults,
): { columnKey: string | null; missing: boolean } {
  const column = effectiveColumn(row, role, defaults);
  if (column == null) return { columnKey: null, missing: true };
  // An association pin that matches no column leaves the role unselected.
  if (column.columnKey === '~:0') return { columnKey: null, missing: false };
  return {
    columnKey: column.columnKey,
    missing: isBlankValue(row.valuesByColumn[column.columnKey]),
  };
}

export function rowHasMissingDesignation(
  row: DesignationVariantRow,
  defaults: DesignationDefaults,
): boolean {
  const sold = effectiveCell(row, 'sold_as', defaults);
  const bought = effectiveCell(row, 'bought_as', defaults);
  if (sold.missing || bought.missing) return true;
  return !sold.columnKey && !bought.columnKey;
}

/** A link-less master has no designation to miss, so it must not sort as a warning. */
export function rowNeedsDesignationWarning(
  row: DesignationVariantRow,
  defaults: DesignationDefaults,
): boolean {
  const hasValue = Object.values(row.valuesByColumn).some(
    (value) => typeof value === 'string' && value.trim() !== '',
  );
  if (!hasValue) return false;
  return rowHasMissingDesignation(row, defaults);
}

export function sortWarningRowsFirst<T extends { eveId: number; warning: boolean }>(rows: readonly T[]): T[] {
  return [...rows].sort((left, right) => {
    if (left.warning !== right.warning) return left.warning ? -1 : 1;
    return left.eveId - right.eveId;
  });
}

export interface LinkedIdentifier {
  type: string;
  value: string;
}

/**
 * Fills only unsaved roles from the leftmost mapping column.
 * A stored role is left unchanged. Column order matches
 * buildVariantManagementColumns: identifier type, then occurrence.
 */
export function leftmostDesignationRef(columnKeys: Iterable<string>): IdentifierRef | null {
  let bestKey: string | null = null;
  let bestType: string | null = null;
  for (const key of columnKeys) {
    const separator = key.indexOf(':');
    if (separator <= 0) continue;
    const identifierType = key.slice(0, separator).trim();
    const type = identifierType.toLowerCase();
    if (!type || type === '~' || key.slice(separator + 1) !== '0') continue;
    if (bestType == null || type.localeCompare(bestType, undefined, { sensitivity: 'base' }) < 0) {
      bestType = type;
      bestKey = key;
    }
  }
  if (!bestKey) return null;
  const separator = bestKey.indexOf(':');
  return { columnKey: bestKey, identifierType: bestKey.slice(0, separator).trim() };
}

export function defaultsWithLeftmostColumn(
  saved: DesignationDefaults,
  columnKeys: Iterable<string>,
): DesignationDefaults {
  if (saved.soldAs && saved.boughtAs) return saved;
  const leftmost = leftmostDesignationRef(columnKeys);
  if (!leftmost) return saved;
  return {
    soldAs: saved.soldAs ?? leftmost,
    boughtAs: saved.boughtAs ?? leftmost,
  };
}

export function firstTimeDefaultRef(
  variants: readonly { eveId: number; identifiers: readonly LinkedIdentifier[] }[],
): IdentifierRef | null {
  if (variants.length === 0) return null;
  const first = [...variants].sort((left, right) => left.eveId - right.eveId)[0];
  const types = [...new Set(first.identifiers.map((identifier) => identifier.type.trim()).filter(Boolean))].sort(
    (left, right) => left.localeCompare(right, undefined, { sensitivity: 'base' }),
  );
  const type = types[0];
  if (!type) return null;
  return { columnKey: `${type}:0`, identifierType: type };
}

export function leftmostReplacement(
  columns: readonly ColumnChoice[],
  removedKey: string,
): ColumnChoice | null {
  return columns.find((column) => column.key !== removedKey) ?? null;
}

export function applyColumnRemoval(input: {
  rows: readonly DesignationVariantRow[];
  defaults: DesignationDefaults;
  removedKey: string;
  replacement: IdentifierRef | null;
}): ApplyReady {
  const roles: DesignationRole[] = [];
  if (input.defaults.soldAs?.columnKey === input.removedKey) roles.push('sold_as');
  if (input.defaults.boughtAs?.columnKey === input.removedKey) roles.push('bought_as');

  const nextDefaults: DesignationDefaults = {
    soldAs: roles.includes('sold_as') ? input.replacement : input.defaults.soldAs,
    boughtAs: roles.includes('bought_as') ? input.replacement : input.defaults.boughtAs,
  };

  const rows = input.rows.map((row) => {
    let nextRow = { ...row };
    for (const role of roles) {
      const override = overrideFor(row, role);
      if (override?.columnKey === input.removedKey) nextRow = clearRole(nextRow, role);
    }
    return nextRow;
  });

  return {
    ok: true,
    conflict: false,
    defaults: nextDefaults,
    rows,
    events: input.rows.map((row, index) =>
      historyFor(row, rows[index], 'changed', roles, 'column_removed'),
    ),
  };
}

export function designationForNewRow(
  defaults: DesignationDefaults,
  valuesByColumn: Record<string, string | null | undefined>,
): { soldAsMissing: boolean; boughtAsMissing: boolean } {
  const row: DesignationVariantRow = {
    masterProductId: 'new',
    eveId: 0,
    soldAsOverride: null,
    boughtAsOverride: null,
    valuesByColumn,
  };
  return {
    soldAsMissing: effectiveCell(row, 'sold_as', defaults).missing,
    boughtAsMissing: effectiveCell(row, 'bought_as', defaults).missing,
  };
}
